// A small markdown reader for briefs and notes. It builds React elements, so
// nothing an agent writes is ever parsed as HTML.

import type { ReactNode } from "react";
import { safeHref } from "../../shared/extract";
import { textColor } from "../../shared/textColors";
import { linkGuard, linkMarks } from "../lib/Linked";
import { gluedAt, linkParts, ownWords } from "../lib/links";

/** <span data-color="orange">words</span>, as a page writes coloured words (Features 1, 20 Oct). */
const COLOR_SPAN = /<span data-colou?r=["']?([A-Za-z]+)["']?>(.*?)<\/span>/gi;

/** A line's words, coloured where it says so; any other span stays as written. */
function words(text: string, key = 0): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let seg = 0;
  let m: RegExpExecArray | null;
  COLOR_SPAN.lastIndex = 0;
  while ((m = COLOR_SPAN.exec(text))) {
    const c = textColor(m[1]);
    if (!c) continue;
    if (m.index > last) out.push(...inline(text.slice(last, m.index), key * 37 + seg++));
    out.push(
      <span key={`c${key}-${seg}`} className="tc" data-color={c}>
        {inline(m[2], key * 37 + seg++)}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...inline(text.slice(last), key * 37 + seg));
  return out;
}

function inline(text: string, key = 0): ReactNode[] {
  const out: ReactNode[] = [];
  // a bare address (the last one) is read by the board's one link rule (src/lib/links.ts), so a full stop or a
  // bracket after it stays text and www. works, as everywhere else on the board (Features 2, 21 Oct)
  const re = /(\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\s][^*]*)\*|_([^_\s][^_]*)_|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)|~~([^~]+)~~|((?:https?:\/\/|www\.)[^\s<>"]+))/gi;
  let last = 0;
  let m: RegExpExecArray | null;
  let n = key * 1000;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[2] || m[3]) out.push(<strong key={n++}>{m[2] ?? m[3]}</strong>);
    else if (m[4] || m[5]) out.push(<em key={n++}>{m[4] ?? m[5]}</em>);
    else if (m[6]) out.push(<code key={n++}>{m[6]}</code>);
    else if (m[7]) {
      const href = safeHref(m[8]);
      // its own words, as everywhere on the board, unless they read as another site's address (Round 7)
      const shown = href ? ownWords(m[7], href, m[8]) : m[7];
      out.push(
        href ? (
          <a key={n++} href={href} target="_blank" rel="noreferrer noopener" data-interactive {...linkMarks(href, shown, m[0])} {...linkGuard}>
            {shown}
          </a>
        ) : (
          m[7]
        ),
      );
    } else if (m[9]) out.push(<s key={n++}>{m[9]}</s>);
    else if (m[10]) {
      const parts = gluedAt(text, m.index) ? [{ text: m[10] }] : linkParts(m[10]);
      for (const p of parts) {
        if (!p.href) {
          out.push(p.text);
          continue;
        }
        // the board's one short name for an address (src/lib/links.ts linkLabel)
        const shown = p.label ?? p.text;
        out.push(
          <a key={n++} href={p.href} target="_blank" rel="noreferrer noopener" data-interactive {...linkMarks(p.href, shown, p.text)} {...linkGuard}>
            {shown}
          </a>,
        );
      }
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ text, className = "md" }: { text: string; className?: string }) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const content = words(h[2], k);
      blocks.push(level === 1 ? <h1 key={k++}>{content}</h1> : level === 2 ? <h2 key={k++}>{content}</h2> : <h3 key={k++}>{content}</h3>);
      i++;
      continue;
    }
    if (/^```/.test(line)) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) code.push(lines[i++]);
      i++;
      blocks.push(<pre key={k++}>{code.join("\n")}</pre>);
      continue;
    }
    if (/^>\s?/.test(line)) {
      const q: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) q.push(lines[i++].replace(/^>\s?/, ""));
      blocks.push(<blockquote key={k++}>{words(q.join(" "), k)}</blockquote>);
      continue;
    }
    if (/^(\s*)([-*+]|\d+[.)])\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]\s+/.test(line);
      const items: ReactNode[] = [];
      while (i < lines.length && /^(\s*)([-*+]|\d+[.)])\s+/.test(lines[i])) {
        const raw = lines[i].replace(/^(\s*)([-*+]|\d+[.)])\s+/, "");
        const task = /^\[( |x|X)\]\s+(.*)$/.exec(raw);
        items.push(
          task ? (
            <li key={i} className={task[1].trim() ? "task done" : "task"}>
              <span className="md-check" aria-hidden />
              {words(task[2], i)}
            </li>
          ) : (
            <li key={i}>{words(raw, i)}</li>
          ),
        );
        i++;
      }
      blocks.push(ordered ? <ol key={k++}>{items}</ol> : <ul key={k++}>{items}</ul>);
      continue;
    }
    if (/^(-{3,}|\*{3,})$/.test(line.trim())) {
      blocks.push(<hr key={k++} />);
      i++;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|```|>|\s*([-*+]|\d+[.)])\s+)/.test(lines[i])) para.push(lines[i++]);
    blocks.push(<p key={k++}>{words(para.join(" "), k)}</p>);
  }
  return <div className={className}>{blocks}</div>;
}
